import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person } from '@core/data-access/models/person.model';
import {
  SavedTicketHolderForm,
  SavedTicketHolders,
  SavedTicketHoldersStoreForms
} from '@core/features/account/features/saved-ticket-holders/models/saved-ticket-holders.store.model';
import {
  TicketAssignmentStoreForm,
  TicketAssignmentStoreForms
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { disableAddressFormField } from '@core/utils/form-fields/form-field-helpers';
import { cloneDeep, forEach, forIn, isEmpty, isNil, keys, pickBy } from 'lodash';

export const setFormFields = <T extends { forms: Partial<Record<FormName, { formFields: FormFields }>> }>(
  state: T,
  formFields: FormFields,
  formName: FormName,
  isQuestionnaire: boolean = false
) => {
  if (isEmpty(formFields)) {
    return state;
  }

  const updatedForms: Partial<Record<FormName, { formFields: FormFields }>> = {};

  const clonedForm = cloneDeep(state.forms[formName]);

  const clonedFormFields = clonedForm?.formFields;

  forIn(formFields, (formField, formFieldKey) => {
    if (isQuestionnaire !== formField.isQuestionnaire) {
      return;
    }

    const clonedFormField = !isNil(clonedFormFields) && clonedFormFields[formFieldKey];

    if (isNil(updatedForms[formName])) {
      updatedForms[formName] = clonedForm;
    }

    updatedForms[formName].formFields[formFieldKey] = isNil(clonedFormField)
      ? formField
      : {
          ...clonedFormField,
          ...formField
        };
  });

  if (isEmpty(updatedForms)) {
    return state;
  }

  return {
    ...state,
    forms: {
      ...state.forms,
      [formName]: updatedForms[formName]
    }
  };
};

export const setTicketHolderFormFields = <T extends { forms: TicketAssignmentStoreForms }>(
  state: T,
  person: Person,
  formFields: FormFields,
  formName: FormName,
  isQuestionnaire: boolean = false
) => {
  if (isEmpty(formFields)) {
    return state;
  }

  const updatedForms: Partial<TicketAssignmentStoreForms> = {};

  const clonedStateFormForms = cloneDeep(state.forms[formName]);

  const updateFormsTariffUuids = keys(
    pickBy(clonedStateFormForms, (form: TicketAssignmentStoreForm) => form?.person?.uuid === person?.uuid)
  );

  forEach(updateFormsTariffUuids, (tariffUuid) => {
    forIn(formFields, (formField, formFieldKey) => {
      if (isQuestionnaire !== formField.isQuestionnaire) {
        return;
      }

      const clonedFormFields = clonedStateFormForms[tariffUuid]?.formFields;
      const clonedFormField = !isNil(clonedFormFields) && clonedFormFields[formFieldKey];

      if (isNil(updatedForms[formName])) {
        updatedForms[formName] = clonedStateFormForms;
        updatedForms[formName][tariffUuid] = clonedStateFormForms[tariffUuid];
      }

      updatedForms[formName][tariffUuid].formFields[formFieldKey] = isNil(clonedFormField)
        ? formField
        : {
            ...clonedFormField,
            ...formField
          };
    });

    disableAddressFormField(updatedForms[formName][tariffUuid].formFields);
  });

  if (isEmpty(updatedForms)) {
    return state;
  }

  return {
    ...state,
    forms: {
      ...state.forms,
      ...updatedForms
    }
  };
};

export const setSavedTicketHolderFormFields = <T extends { forms: SavedTicketHoldersStoreForms }>(
  state: T,
  person: Person,
  formFields: FormFields,
  formName: FormName
) => {
  if (isEmpty(formFields)) {
    return state;
  }

  const updatedForms: Partial<SavedTicketHoldersStoreForms> = {};

  const clonedStateFormForms = cloneDeep(state.forms[formName]);

  const updateFormsPersonUuids = keys(
    pickBy(clonedStateFormForms, (form: SavedTicketHolderForm) => form?.person?.uuid === person?.uuid)
  );

  forEach(updateFormsPersonUuids, (personUuid) => {
    forIn(formFields, (formField, formFieldKey) => {
      if (formField.isQuestionnaire) {
        return;
      }

      const clonedFormFields = clonedStateFormForms[personUuid]?.formFields;
      const clonedFormField = !isNil(clonedFormFields) && clonedFormFields[formFieldKey];

      if (isNil(updatedForms[formName])) {
        updatedForms[formName] = clonedStateFormForms;
        updatedForms[formName][personUuid] = clonedStateFormForms[personUuid];
      }

      updatedForms[formName][personUuid].formFields[formFieldKey] = isNil(clonedFormField)
        ? formField
        : {
            ...clonedFormField,
            ...formField
          };
    });
  });

  if (isEmpty(updatedForms)) {
    return state;
  }

  return {
    ...state,
    forms: {
      ...state.forms,
      ...updatedForms
    }
  };
};

export const setSavedTicketHolderFormFieldsWithoutSaving = <
  T extends { [FormName.SavedTicketHolders]: SavedTicketHolders }
>(
  state: T,
  person: Person,
  formFields: FormFields,
  isQuestionnaire: boolean = false
) => {
  if (isEmpty(formFields)) {
    return state;
  }

  const { uuid } = person;

  const updatedSavedTicketHolders: SavedTicketHolders = {};

  const clonedSavedTicketHolders = cloneDeep(state.savedTicketHolders);
  const clonedFormFields = clonedSavedTicketHolders[uuid]?.formFields;

  forIn(formFields, (formField, formFieldKey) => {
    if (isQuestionnaire !== formField.isQuestionnaire) {
      return;
    }

    const clonedFormField = !isNil(clonedFormFields) && clonedFormFields[formFieldKey];

    if (isNil(updatedSavedTicketHolders[uuid])) {
      updatedSavedTicketHolders[uuid] = clonedSavedTicketHolders[uuid];
    }

    updatedSavedTicketHolders[uuid].formFields[formFieldKey] = isNil(clonedFormField)
      ? formField
      : {
          ...clonedFormField,
          ...formField
        };
  });

  if (isEmpty(updatedSavedTicketHolders)) {
    return state;
  }

  return {
    ...state,
    savedTicketHolders: {
      ...state.savedTicketHolders,
      ...updatedSavedTicketHolders
    }
  };
};
